import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Modal, Space, Spin, Typography } from "antd";
import {
  DownloadOutlined,
  EyeOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import {
  downloadPetitionDocument,
  fetchPetitionDocumentInfo,
  fetchPetitionDocumentPreview,
} from "../utils/petitionTrackerApi";

const DPAT_PRIMARY = "#16325a";
const PETITION_DOCUMENT_VIEWER_HEIGHT = "min(75vh, 800px)";

function PetitionDocumentPanel({
  districtId,
  year,
  districtName = "",
  petitionComment = null,
  className = "",
}) {
  const [petitionDocument, setPetitionDocument] = useState(null);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewError, setPreviewError] = useState("");
  const previewBlobUrlRef = useRef(null);

  const trackerMeta = petitionComment?.dddpData?.petitionTracker || {};

  useEffect(() => {
    setPetitionDocument(null);
    setDocumentLoading(Boolean(petitionComment && districtId && year != null));
  }, [districtId, year, petitionComment]);

  useEffect(() => {
    let cancelled = false;

    const loadPetitionDocument = async () => {
      if (!districtId || year == null || !petitionComment) {
        if (!cancelled) {
          setPetitionDocument(null);
          setDocumentLoading(false);
        }
        return;
      }

      try {
        setDocumentLoading(true);
        const documentInfo = await fetchPetitionDocumentInfo({
          orgUnitId: districtId,
          year,
          districtName,
          trackedEntityId: trackerMeta.trackedEntityId,
          fileResourceId: trackerMeta.fileResourceId,
          eventId: trackerMeta.eventId,
        });

        if (!cancelled) {
          setPetitionDocument(
            documentInfo?.fileResourceId || documentInfo?.eventId ? documentInfo : null,
          );
        }
      } catch (error) {
        console.error("Failed to fetch petition document:", error);
        if (!cancelled) setPetitionDocument(null);
      } finally {
        if (!cancelled) setDocumentLoading(false);
      }
    };

    loadPetitionDocument();
    return () => {
      cancelled = true;
    };
  }, [
    districtId,
    year,
    districtName,
    petitionComment,
    trackerMeta.trackedEntityId,
    trackerMeta.fileResourceId,
    trackerMeta.eventId,
  ]);

  const revokePreviewBlobUrl = useCallback(() => {
    if (previewBlobUrlRef.current) {
      URL.revokeObjectURL(previewBlobUrlRef.current);
      previewBlobUrlRef.current = null;
    }
  }, []);

  const closeDocumentViewer = useCallback(() => {
    setViewerOpen(false);
    setPreviewUrl("");
    setPreviewError("");
    setPreviewLoading(false);
    revokePreviewBlobUrl();
  }, [revokePreviewBlobUrl]);

  useEffect(() => () => revokePreviewBlobUrl(), [revokePreviewBlobUrl]);

  const handleViewDocument = async () => {
    if (!petitionDocument?.fileResourceId && !petitionDocument?.eventId) return;

    setViewerOpen(true);
    setPreviewLoading(true);
    setPreviewUrl("");
    setPreviewError("");
    revokePreviewBlobUrl();

    try {
      const { blobUrl } = await fetchPetitionDocumentPreview({
        fileResourceId: petitionDocument.fileResourceId,
        eventId: petitionDocument.eventId,
        eventFileDownloadUrl: petitionDocument.eventFileDownloadUrl,
        fileName: petitionDocument.fileName,
      });
      previewBlobUrlRef.current = blobUrl;
      setPreviewUrl(blobUrl);
    } catch (error) {
      console.error("Failed to preview petition document:", error);
      setPreviewError("Could not load the petition document for preview. Try downloading instead.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadDocument = async () => {
    if (!petitionDocument?.fileResourceId && !petitionDocument?.eventId) return;

    try {
      setDownloadLoading(true);
      await downloadPetitionDocument({
        fileResourceId: petitionDocument.fileResourceId,
        fileName: petitionDocument.fileName || "petition-document.pdf",
        eventId: petitionDocument.eventId,
        eventFileDownloadUrl: petitionDocument.eventFileDownloadUrl,
      });
    } catch (error) {
      console.error("Failed to download petition document:", error);
    } finally {
      setDownloadLoading(false);
    }
  };

  if (!petitionComment) {
    return null;
  }

  if (!documentLoading && !petitionDocument?.fileResourceId && !petitionDocument?.eventId) {
    return null;
  }

  return (
    <>
      <div
        className={`mt-4 rounded-lg border px-4 py-3 ${className}`.trim()}
        style={{ borderColor: "#e6ebf1", background: "#fff" }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Space>
            <PaperClipOutlined style={{ color: DPAT_PRIMARY }} />
            <Typography.Text strong>Petition document</Typography.Text>
          </Space>
          {documentLoading ? (
            <Spin size="small" />
          ) : (
            <Space wrap>
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={handleViewDocument}
                style={{ paddingInline: 0 }}
              >
                {petitionDocument?.fileName || "View document"}
              </Button>
              <Button
                type="link"
                icon={<DownloadOutlined />}
                loading={downloadLoading}
                onClick={handleDownloadDocument}
                style={{ paddingInline: 0 }}
              >
                Download
              </Button>
            </Space>
          )}
        </div>
        <Typography.Paragraph type="secondary" style={{ margin: "8px 0 0", fontSize: 12 }}>
          Click the document name to open it in a viewer.
        </Typography.Paragraph>
      </div>

      <Modal
        title={petitionDocument?.fileName || "Petition document"}
        open={viewerOpen}
        onCancel={closeDocumentViewer}
        width={1100}
        style={{ maxWidth: "92vw" }}
        centered
        destroyOnClose
        footer={[
          <Button
            key="download"
            icon={<DownloadOutlined />}
            loading={downloadLoading}
            onClick={handleDownloadDocument}
            disabled={!petitionDocument?.fileResourceId && !petitionDocument?.eventId}
          >
            Download
          </Button>,
          <Button key="close" type="primary" onClick={closeDocumentViewer}>
            Close
          </Button>,
        ]}
        styles={{
          body: {
            padding: previewLoading || previewError ? 16 : 0,
            minHeight: PETITION_DOCUMENT_VIEWER_HEIGHT,
          },
        }}
      >
        {previewLoading ? (
          <div
            className="flex flex-col items-center justify-center gap-3"
            style={{ height: PETITION_DOCUMENT_VIEWER_HEIGHT }}
          >
            <Spin size="large" />
            <Typography.Text type="secondary">Loading document…</Typography.Text>
          </div>
        ) : null}

        {!previewLoading && previewError ? (
          <Alert type="warning" showIcon message={previewError} />
        ) : null}

        {!previewLoading && !previewError && previewUrl ? (
          <iframe
            src={previewUrl}
            title={petitionDocument?.fileName || "Petition document"}
            className="block w-full border-0"
            style={{ height: PETITION_DOCUMENT_VIEWER_HEIGHT }}
          />
        ) : null}
      </Modal>
    </>
  );
}

export default PetitionDocumentPanel;
